import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { closeTopForm } from '../utils/formStack';

// Keyboard "one step back", active on EVERY page (desktop/web counterpart of the
// Android hardware back button in BackButtonHandler.jsx). Both keys behave the
// same way:
//   • Escape    — always
//   • Backspace — only when the caret is NOT in a text field (there it deletes)
// and each press goes back exactly one level:
//   1) an open form / modal → close it and return to whatever is underneath
//      (a nested form such as Add Family returns to the Guest form, the next
//      press closes that one). Typed content is NOT cleared.
//   2) we navigated INTO this page inside the app (nav click, dashboard tile,
//      row drill-in) → navigate back one history step;
//   3) nothing to go back to (login, first page after sign-in) → nothing.
// Depth is counted from the router's own PUSH/POP events rather than a fixed list
// of "root" paths, so a page like /app/members is a root when a plain user lands
// on it but goes back when reached via Functions → row click.

// Where a Backspace must keep its normal "delete" meaning.
function isTypingTarget(el) {
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag !== 'INPUT') return false;
  // Buttons/checkboxes swallow no text, so Backspace there means "go back".
  return !['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'range', 'color'].includes((el.type || 'text').toLowerCase());
}

export default function BackKeyHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const navType = useNavigationType();
  const depth = useRef(0);

  // Track how many in-app steps we can pop. Login/logout use replace, so they
  // never count; a POP (browser back or our own navigate(-1)) unwinds one.
  useEffect(() => {
    if (navType === 'PUSH') depth.current += 1;
    else if (navType === 'POP') depth.current = Math.max(0, depth.current - 1);
  }, [location.key, navType]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      const el = document.activeElement;
      if (e.key === 'Backspace') {
        if (isTypingTarget(el)) return;      // let it delete text
      } else if (e.key !== 'Escape') {
        return;
      } else if (el?.tagName === 'SELECT') {
        return;                              // Escape there just closes the list
      }
      if (closeTopForm()) { e.preventDefault(); return; }
      if (depth.current <= 0) { if (e.key === 'Backspace') e.preventDefault(); return; }
      // A filled input keeps its text; the key simply leaves the page.
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) el.blur();
      e.preventDefault();
      navigate(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  return null;
}
